import { useState, useEffect } from 'react'

import CircleLoader from '@components/CircleLoader'
import ErrorMessage from '@components/ErrorMessage'
import Pin from '@components/Pin'

import { useOnUserChange } from 'auth/hooks'

import { locationDetailsSelector, locationDetailsPendingSelector, vehiclesSelector } from 'booking/selectors'
import { Map } from 'booking/components/Map'
import { ParkingInfoSideBar } from 'booking/components/ParkingInfoSideBar'
import { getBookingLocationDetails } from 'booking/actions'
import { sendGoogleReserveConversionData } from 'analytics/actions'
import { setGoogleReserveCookiesFromUrl, getRwgToken, sendAmplitudeEvent, useSendGTAGEvent } from 'analytics/utils'
import { ANALYTICS_FLOW_TYPE_SUBSCRIPTION } from 'analytics/constants'

import { loadOnMount } from 'shared/hoc'
import withWaitlist from 'shared/hocs/withWaitlist'
import { isDesktop } from 'shared/responsive-helpers'
import { MarkerStyle } from 'shared/generate-marker-style'
import featureToggles from 'shared/featureToggles'
import { useConfirm } from 'shared/hooks/useConfirm'
import scrollTop from 'shared/scroll-top'

import VehicleSelection from 'subscription-flow/components/VehicleSelection'

import {
    FLOW_STEP_DATE,
    FLOW_STEP_PRODUCT,
    FLOW_STEP_REVIEW,
    FLOW_STEP_SUCCESS,
    FLOW_STEP_VEHICLE,
    CREDENTIAL_PHYSICAL,
    PURCHASE_FLOW_HEADER_PREFIX,
    FLOW_STEP_ACCESS_OPTIONS,
    CREDENTIAL_DIGITAL,
} from 'subscription-flow/constants'

import {
    locationNameSelector,
    subscriptionLocationErrorSelector,
    subscriptionLocationLoadingSelector,
    purchaseDataSelector,
    credentialTypeSelector,
    collectAddressLoadingSelector,
    locationSummarySelector,
} from '../selectors'
import { waitlistIdProductSelector } from '../../waitlist/selectors'

import {
    getSubscriptionLocation,
    getOrderSummary,
    buySubscription,
    setPurchaseData,
    getCollectAddress,
} from '../actions'
import { subscribeWaitingList } from '../../waitlist/actions'

import { limitMonths, useFlow as usePurchaseFlowSteps } from './utils'

import DateSelection from './DateSelection'
import ProductSelection from './ProductSelection'
import PurchaseFlowHeader from './PurchaseFlowHeader'
import ReviewPaySelection from './ReviewPaySelection'
import SuccessPage from './SuccessPage'
import AccessOptions from './AccessOptions/AccessOptions'
import WaitlistDateSelection from '../../waitlist/components/WaitlistDateSelection'

import purchaseStyles from 'subscription-flow/styles.module.scss'
import styles from './styles.module.scss'

const SUBSCRIPTION_LITE_MONTHS_LIMIT = 1

const PurchaseFlow = ({
    locationName,
    location,
    locationPending,
    collectAddressLoading,
    subscriptionLocationLoading,
    errorMessage,
    getOrderSummary,
    buySubscription,
    purchaseData,
    setPurchaseData,
    credentialType,
    userVehicles,
    getCollectAddress,
    waitlistAvailable,
    waitlistErrorMsg,
    waitlistId,
    subscribeWaitingList,
    waitingListIdProduct,
    waitlistState,
    isWaitListFlow,
    sendGoogleReserveConversionData,
    locationSummary,
}) => {
    const sendGTAGEvent = useSendGTAGEvent()
    const { confirm, ConfirmModal } = useConfirm()
    const [activeStep, setActiveStep] = useState(FLOW_STEP_DATE)
    const hasCredentialType = type => Boolean(credentialType?.find(item => item === type))
    const showDigitalOption = hasCredentialType(CREDENTIAL_DIGITAL)
    const showPhysicalOption = hasCredentialType(CREDENTIAL_PHYSICAL)

    const hasDelivery =
        !featureToggles.hasSubcriptionLite ||
        (featureToggles.hasSubcriptionLite && hasCredentialType(CREDENTIAL_PHYSICAL)) ||
        (featureToggles.hasSubcriptionLite && hasCredentialType(CREDENTIAL_DIGITAL))

    const flowSteps = usePurchaseFlowSteps(hasDelivery ? [] : [FLOW_STEP_ACCESS_OPTIONS])
    const sendShipChargeId = flowSteps.includes(FLOW_STEP_ACCESS_OPTIONS)

    useEffect(() => {
        if (!purchaseData.startDate) {
            setActiveStep(FLOW_STEP_DATE)
        }
    }, [])

    useEffect(() => {
        if (locationName) {
            sendAmplitudeEvent('Begin Checkout', {
                'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
                'Location Name': locationName,
            })
            sendGTAGEvent('begin_checkout', {
                flow_type: ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
                location_name: locationName,
            })
        }
    }, [locationName])

    useEffect(() => {
        if (locationSummary.code && getRwgToken()) {
            setGoogleReserveCookiesFromUrl(locationSummary.code)
        }
    }, [locationSummary])

    useEffect(() => {
        scrollTop()
    }, [activeStep])

    useEffect(() => {
        isWaitListFlow && waitlistId && activeStep === FLOW_STEP_SUCCESS && subscribeWaitingList()
    }, [activeStep])

    useEffect(() => {
        activeStep === FLOW_STEP_SUCCESS && sendGoogleReserveConversionData(locationSummary.code)
    }, [activeStep])

    useOnUserChange(isLogin => {
        if (!isLogin && purchaseData.vehicles?.length > 0) {
            setPurchaseData({ ...purchaseData, vehicles: null })
            setActiveStep(FLOW_STEP_VEHICLE)
        }
    })

    const goNext = () => {
        const newIndex = flowSteps.indexOf(activeStep) + 1

        if (newIndex < flowSteps.length) {
            setActiveStep(flowSteps[newIndex])
        }

        switch (flowSteps[newIndex]) {
            case FLOW_STEP_ACCESS_OPTIONS:
                if (hasCredentialType(CREDENTIAL_PHYSICAL)) {
                    getCollectAddress()
                }
                break
            case FLOW_STEP_REVIEW:
                getOrderSummary({ ...purchaseData })
                break
        }
    }

    const setStartDate = startDate => {
        setPurchaseData({ ...purchaseData, startDate })
        sendAmplitudeEvent('Selected Date', {
            'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
            'Location Name': locationName,
        })
        sendGTAGEvent('select_date', {
            flow_type: ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
            location_name: locationName,
        })
        goNext()
    }

    const setProduct = product => {
        setPurchaseData({ ...purchaseData, product })
        sendAmplitudeEvent('Selected Product', {
            'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
            'Location Name': locationName,
        })
        sendGTAGEvent('select_product', {
            flow_type: ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
            location_name: locationName,
        })
        goNext()
    }

    const setVehicle = vehicles => {
        setPurchaseData({ ...purchaseData, vehicles })
        sendAmplitudeEvent('Vehicle Selected', { 'Flow Type': ANALYTICS_FLOW_TYPE_SUBSCRIPTION })
        sendGTAGEvent('select_vehicle', {
            flow_type: ANALYTICS_FLOW_TYPE_SUBSCRIPTION,
        })
        goNext()
    }

    const setDelivery = (delivery, deliveryAddress) => {
        setPurchaseData({ ...purchaseData, delivery, deliveryAddress })
        goNext()
    }

    const handleBuy = (paymentMethod, billingAddress, deliveryAddress, termsAcceptedDate) => {
        buySubscription({
            ...purchaseData,
            paymentMethod,
            billingAddress,
            deliveryAddress,
            termsAcceptedDate,
            sendShipChargeId,
        })
    }

    const setSuccess = () => {
        setActiveStep(FLOW_STEP_SUCCESS)
    }

    const onStepClick = async step => {
        let { product, delivery, vehicles } = purchaseData
        if (step === FLOW_STEP_PRODUCT) {
            product = null
            vehicles = null
            delivery = null
        }
        if (step === FLOW_STEP_VEHICLE) {
            vehicles = null
            delivery = null
        }
        if (step === FLOW_STEP_ACCESS_OPTIONS) {
            delivery = null
        }

        if (step === FLOW_STEP_DATE) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }

            product = null
            vehicles = null
            delivery = null
        }

        setActiveStep(step)
        setPurchaseData({ ...purchaseData, product, delivery, vehicles })
    }

    const loading = subscriptionLocationLoading || collectAddressLoading

    return (
        <div className={styles.purchaseFlowContainer}>
            <ConfirmModal
                title="subscription.purchase.step.confirm.title"
                text="subscription.purchase.step.confirm.text"
            />
            <div className={styles.purchaseScreen}>
                {activeStep === FLOW_STEP_SUCCESS ? (
                    <SuccessPage />
                ) : (
                    <div className={purchaseStyles.flowContainer}>
                        {loading && <CircleLoader />}
                        {errorMessage && <ErrorMessage messages={[errorMessage]} />}
                        {!loading && !errorMessage && (
                            <div className={purchaseStyles.flowSection}>
                                <PurchaseFlowHeader
                                    activeStep={activeStep}
                                    stepValues={purchaseData}
                                    location={locationName}
                                    onStepClick={onStepClick}
                                    flowSteps={flowSteps}
                                    stepPrefix={PURCHASE_FLOW_HEADER_PREFIX}
                                />
                                {activeStep === FLOW_STEP_DATE &&
                                    (isWaitListFlow && waitlistId ? (
                                        <WaitlistDateSelection
                                            waitlistState={waitlistState}
                                            waitlistAvailable={waitlistAvailable}
                                            onNext={setStartDate}
                                            errorMsg={waitlistErrorMsg}
                                        />
                                    ) : (
                                        <DateSelection
                                            onNext={setStartDate}
                                            value={purchaseData.startDate?.value}
                                            isOutsideRange={
                                                featureToggles.hasSubcriptionLite
                                                    ? limitMonths(SUBSCRIPTION_LITE_MONTHS_LIMIT)
                                                    : null
                                            }
                                        />
                                    ))}
                                {activeStep === FLOW_STEP_PRODUCT && (
                                    <ProductSelection
                                        waitingListIdProduct={waitingListIdProduct}
                                        onNext={setProduct}
                                        startDate={purchaseData.startDate}
                                        value={purchaseData.product}
                                        isWaitListFlow={isWaitListFlow}
                                    />
                                )}
                                {activeStep === FLOW_STEP_VEHICLE && (
                                    <VehicleSelection
                                        noLimit
                                        onNext={setVehicle}
                                        flowSelectedVehicles={purchaseData.vehicles || userVehicles}
                                        analyticsFlowType={ANALYTICS_FLOW_TYPE_SUBSCRIPTION}
                                    />
                                )}
                                {activeStep === FLOW_STEP_ACCESS_OPTIONS && (
                                    <AccessOptions
                                        showDigitalOption={showDigitalOption}
                                        showPhysicalOption={showPhysicalOption}
                                        onNext={setDelivery}
                                        startDate={purchaseData.startDate?.displayValue}
                                        value={purchaseData.delivery}
                                    />
                                )}
                                {activeStep === FLOW_STEP_REVIEW && !errorMessage && (
                                    <ReviewPaySelection
                                        onSuccess={setSuccess}
                                        purchaseData={purchaseData}
                                        handleBuy={handleBuy}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isDesktop() && Object.keys(location).length > 0 && !loading && (
                <div className={purchaseStyles.sidebar}>
                    <ParkingInfoSideBar loading={locationPending} location={location}>
                        {location.geoLocation && (
                            <Map geoLocation={location.geoLocation}>
                                <div style={MarkerStyle(30, 30)}>
                                    <Pin name={location.name} />
                                </div>
                            </Map>
                        )}
                    </ParkingInfoSideBar>
                </div>
            )}
        </div>
    )
}

export default compose(
    withWaitlist,
    connect(
        createStructuredSelector({
            locationName: locationNameSelector,
            errorMessage: subscriptionLocationErrorSelector,
            subscriptionLocationLoading: subscriptionLocationLoadingSelector,
            locationPending: locationDetailsPendingSelector,
            collectAddressLoading: collectAddressLoadingSelector,
            location: locationDetailsSelector,
            credentialType: credentialTypeSelector,
            purchaseData: purchaseDataSelector,
            userVehicles: vehiclesSelector,
            waitingListIdProduct: waitlistIdProductSelector,
            locationSummary: locationSummarySelector,
        }),
        {
            getSubscriptionLocation,
            getBookingLocationDetails,
            getOrderSummary,
            buySubscription,
            setPurchaseData,
            getCollectAddress,
            subscribeWaitingList,
            sendGoogleReserveConversionData,
        }
    ),
    loadOnMount('getSubscriptionLocation', ({ match }) => [match?.params?.locationCode || match.params?.waitingListId]),
    loadOnMount('getBookingLocationDetails', ({ match }) => [
        match?.params?.locationCode || match.params?.waitingListId,
    ])
)(PurchaseFlow)
